import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Photo } from '../../types/Photo';
import { getAllPhotos, deletePhoto, deleteImage, updatePhotoOrders, initializePhotoOrders } from '../../services/photoService';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: #333;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const AddButton = styled.button`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  background: #0070f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #0051cc;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const InitButton = styled.button`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const Card = styled.div<{ $isDragging?: boolean }>`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: ${(props) => (props.$isDragging ? 'grabbing' : 'grab')};
  opacity: ${(props) => (props.$isDragging ? 0.5 : 1)};
  transition: opacity 0.2s ease;
`;

const DragHandle = styled.div`
  padding: 8px;
  background: #f8f8f8;
  text-align: center;
  font-size: 12px;
  color: #999;
  border-bottom: 1px solid #e0e0e0;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f0f0f0;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Content = styled.div`
  padding: 16px;
`;

const PhotoTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const Location = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #666;
`;

const DateText = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #999;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button<{ variant?: 'primary' | 'danger' }>`
  flex: 1;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === 'danger'
      ? `
    background: #dc3545;
    color: white;

    &:hover {
      background: #c82333;
    }
  `
      : `
    background: #0070f3;
    color: white;

    &:hover {
      background: #0051cc;
    }
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #d32f2f;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;

  h3 {
    font-size: 24px;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    color: #999;
    margin-bottom: 24px;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;

const ModalText = styled.p`
  margin: 0 0 24px 0;
  font-size: 16px;
  color: #555;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === 'danger'
      ? `
    background: #dc3545;
    color: white;

    &:hover {
      background: #c82333;
    }
  `
      : `
    background: #f0f0f0;
    color: #333;

    &:hover {
      background: #e0e0e0;
    }
  `}
`;

interface SortablePhotoCardProps {
  photo: Photo;
  onEdit: (id: string) => void;
  onDelete: (photo: Photo) => void;
  formatDate: (date: Date | string) => string;
}

const SortablePhotoCard: React.FC<SortablePhotoCardProps> = ({ photo, onEdit, onDelete, formatDate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card $isDragging={isDragging}>
        <DragHandle {...attributes} {...listeners}>
          ⋮⋮ Drag to reorder
        </DragHandle>
        <ImageContainer>
          <Image src={photo.imageURL} alt={photo.title} loading="lazy" />
        </ImageContainer>
        <Content>
          <PhotoTitle>{photo.title}</PhotoTitle>
          <Location>📍 {photo.location}</Location>
          <DateText>{formatDate(photo.date)}</DateText>
          <ButtonGroup>
            <Button variant="primary" onClick={() => onEdit(photo.id)}>
              Edit
            </Button>
            <Button variant="danger" onClick={() => onDelete(photo)}>
              Delete
            </Button>
          </ButtonGroup>
        </Content>
      </Card>
    </div>
  );
};

const AdminPhotoList: React.FC = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const fetchedPhotos = await getAllPhotos();
      setPhotos(fetchedPhotos);
      setError(null);
    } catch (err) {
      setError('Failed to load photos. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeOrders = async () => {
    if (!window.confirm('This will update all photos with order numbers based on their current date. Continue?')) {
      return;
    }

    try {
      setInitializing(true);
      await initializePhotoOrders();
      await fetchPhotos();
      alert('Successfully initialized order fields for all photos!');
    } catch (error) {
      console.error('Error initializing orders:', error);
      alert('Failed to initialize order fields. Please try again.');
    } finally {
      setInitializing(false);
    }
  };

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new (Date as any)(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleEdit = (photoId: string) => {
    navigate(`/admin/edit/${photoId}`);
  };

  const handleDeleteClick = (photo: Photo) => {
    setPhotoToDelete(photo);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!photoToDelete) return;

    try {
      setDeleting(true);

      // Delete the photo from Firestore
      await deletePhoto(photoToDelete.id);

      // Try to delete the image from Storage
      try {
        await deleteImage(photoToDelete.imageURL);
      } catch (error) {
        console.warn('Could not delete image from storage:', error);
      }

      // Update the local state
      setPhotos((prev) => prev.filter((p) => p.id !== photoToDelete.id));

      // Close the modal
      setDeleteModalOpen(false);
      setPhotoToDelete(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setPhotoToDelete(null);
  };

  const handleAddNew = () => {
    navigate('/admin/upload');
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = photos.findIndex((p) => p.id === active.id);
    const newIndex = photos.findIndex((p) => p.id === over.id);

    const newPhotos = arrayMove(photos, oldIndex, newIndex);

    // Update local state immediately for better UX
    setPhotos(newPhotos);

    // Update orders in Firebase
    try {
      const photoOrders = newPhotos.map((photo, index) => ({
        id: photo.id,
        order: index,
      }));
      await updatePhotoOrders(photoOrders);
    } catch (error) {
      console.error('Error updating photo orders:', error);
      // Revert on error
      fetchPhotos();
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading photos...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>{error}</ErrorContainer>
      </Container>
    );
  }

  if (photos.length === 0) {
    return (
      <Container>
        <Header>
          <Title>Manage Photos</Title>
          <HeaderButtons>
            <AddButton onClick={handleAddNew}>Add New Photo</AddButton>
          </HeaderButtons>
        </Header>
        <EmptyState>
          <h3>No Photos Yet</h3>
          <p>Start adding photos to your portfolio.</p>
          <AddButton onClick={handleAddNew}>Upload Your First Photo</AddButton>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Manage Photos ({photos.length})</Title>
        <HeaderButtons>
          <InitButton onClick={handleInitializeOrders} disabled={initializing}>
            {initializing ? 'Initializing...' : 'Initialize Order'}
          </InitButton>
          <AddButton onClick={handleAddNew}>Add New Photo</AddButton>
        </HeaderButtons>
      </Header>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={photos.map((p) => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <Grid>
            {photos.map((photo) => (
              <SortablePhotoCard
                key={photo.id}
                photo={photo}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                formatDate={formatDate}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      <Modal isOpen={deleteModalOpen}>
        <ModalContent>
          <ModalTitle>Delete Photo?</ModalTitle>
          <ModalText>
            Are you sure you want to delete "{photoToDelete?.title}"? This action cannot be undone.
          </ModalText>
          <ModalButtons>
            <ModalButton onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </ModalButton>
            <ModalButton variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </ModalButton>
          </ModalButtons>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminPhotoList;
